// Feb 2026 — Special-Ed Classroom Tools Pack (free lead magnet).
//
// Ungated public page at /support-tools. Lists five blank, teacher-
// fillable PDF templates as a preview grid; each card has its own
// "Download PDF" button. A prominent "Download the full pack" CTA
// sits above the grid. Optional newsletter line at the bottom —
// never gates a download.
//
// Pattern mirrors /posters (Phase 139). Templates are rendered on
// the HTML engine + cached to disk in support_tools_pack_service.py.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Archive,
  ArrowRight,
  CalendarDays,
  CheckSquare,
  ArrowRightLeft,
  Award,
  Smile,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import DownloadPdfButton from "../components/DownloadPdfButton";
import TrilliumIcon from "../components/TrilliumIcon";
import useDocumentMeta from "../lib/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Slug → preview chrome. Each tool gets a small CSS thumbnail (icon
// + label) that overlays the top of a real page-1 PNG preview of the
// PDF the teacher will download.
const PREVIEW_BY_SLUG = {
  checklist: { Icon: CheckSquare, accent: "#3EB489" },
  "visual-schedule": { Icon: CalendarDays, accent: "#5b8fc2" },
  "first-then": { Icon: ArrowRightLeft, accent: "#8c79c2" },
  "reward-chart": { Icon: Award, accent: "#e89b56" },
  "regulation-scale": { Icon: Smile, accent: "#c25b5b" },
};

const downloadPdf = async (url, filename) => {
  const { data } = await axios.get(url, { responseType: "blob" });
  const objectUrl = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
};

function ToolPreview({ slug, label }) {
  // Real page-1 raster of the actual PDF — generated server-side via
  // PyMuPDF from the same cached PDF the teacher downloads, so the
  // preview never drifts from the file.
  const meta = PREVIEW_BY_SLUG[slug] || { Icon: Sparkles, accent: "#4a6984" };
  const { Icon, accent } = meta;
  return (
    <div className="relative aspect-[3/4] bg-[#f9fafb] border-b border-[#4a6984]/10 overflow-hidden">
      <img
        src={`${API}/support-tools/preview/${encodeURIComponent(slug)}.png`}
        alt={`Preview of the ${label || slug} blank template`}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      {/* Subtle top tag so the card hierarchy still reads as a
          gallery item, not a flat PDF render. */}
      <div
        className="absolute top-2.5 left-2.5 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm rounded-full pl-2 pr-2.5 py-1 shadow-sm"
        aria-hidden="true"
      >
        <Icon className="h-3 w-3" style={{ color: accent }} />
        <span className="font-extrabold text-[#1c2d3d] text-[10px] uppercase tracking-[0.16em]">
          Blank template
        </span>
      </div>
    </div>
  );
}

function ToolCard({ tool, downloading, onDownload }) {
  return (
    <article
      data-testid={`support-tool-card-${tool.slug}`}
      className="rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden flex flex-col"
    >
      <ToolPreview slug={tool.slug} label={tool.label} />
      <div className="p-5 flex flex-col flex-1 gap-3">
        <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight">
          {tool.label}
        </h3>
        <p className="text-sm text-[#4a6984]/85 leading-relaxed flex-1">
          {tool.blurb}
        </p>
        <DownloadPdfButton
          onClick={() => onDownload(tool)}
          loading={downloading}
          disabled={downloading}
          testId={`support-tool-download-${tool.slug}`}
          label="Download PDF"
          className="mt-1"
        />
      </div>
    </article>
  );
}

export default function SupportToolsPage() {
  useDocumentMeta({
    title: "Free special-education classroom tools — Atomic Edu",
    description:
      "Free printable special-education classroom tools — checklists, visual schedules, first-then boards, reward charts, and a daily regulation scale. Blank templates, ready for your classroom.",
  });
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingSlug, setDownloadingSlug] = useState("");
  const [downloadingPack, setDownloadingPack] = useState(false);
  // Cumulative download counter — hidden entirely until the
  // backend's threshold is crossed so early days don't render
  // awkwardly. Numbers are cumulative across all 5 single tools +
  // the combined pack.
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [listRes, statsRes] = await Promise.all([
          axios.get(`${API}/support-tools/list`),
          axios.get(`${API}/support-tools/stats`).catch(() => null),
        ]);
        if (cancelled) return;
        setTools(listRes.data?.tools || []);
        if (statsRes?.data) setStats(statsRes.data);
      } catch {
        if (!cancelled)
          setError("Couldn't load the tools list — please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSingle = async (tool) => {
    setDownloadingSlug(tool.slug);
    try {
      const safe = tool.label.replace(/&/g, "and").replace(/\s+/g, "_");
      await downloadPdf(
        `${API}/support-tools/single/${encodeURIComponent(tool.slug)}`,
        `AtomicEdu_Classroom_Tool_${safe}.pdf`,
      );
    } catch {
      // intentional: button just resets
    } finally {
      setDownloadingSlug("");
    }
  };

  const handlePack = async () => {
    setDownloadingPack(true);
    try {
      await downloadPdf(
        `${API}/support-tools/pack`,
        "AtomicEdu_Classroom_Tools_Pack.pdf",
      );
    } catch {
      // intentional
    } finally {
      setDownloadingPack(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7] text-[#4a6984]">
      {/* Slim public header — mirrors /posters */}
      <header className="bg-[#1c2d3d] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="support-tools-home-link"
          >
            <TrilliumIcon className="h-7 w-7 text-[#96ded1]" />
            <span className="font-black text-lg tracking-tight">Atomic Edu</span>
          </Link>
          <Link
            to="/classroom-support"
            data-testid="support-tools-hub-link"
            className="text-sm font-bold text-white/85 hover:text-white"
          >
            Classroom Support Hub &rarr;
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pt-10 pb-20">
        {/* Hero — value first, colleague to colleague */}
        <section className="max-w-3xl">
          <p
            data-testid="support-tools-eyebrow"
            className="font-extrabold uppercase tracking-[0.22em] text-xs text-[#4a6984]/75 flex items-center gap-1.5"
          >
            <Sparkles className="h-3.5 w-3.5" /> Free for Ontario classrooms
          </p>
          <h1
            data-testid="support-tools-headline"
            className="mt-4 font-black text-4xl sm:text-5xl text-[#4a6984] tracking-tighter leading-[1.05]"
          >
            Special-ed classroom tools
            <br />
            <span className="text-[#1c2d3d]">printable, blank, ready to use.</span>
          </h1>
          <p className="mt-4 text-[#4a6984]/85 font-medium text-base sm:text-lg leading-relaxed">
            Free printable special-education classroom tools — checklists,
            visual schedules, first-then boards, reward charts, and a daily
            regulation scale. Blank templates, ready for your classroom. Print
            the full pack or just the ones you need.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 flex-wrap">
            <DownloadPdfButton
              onClick={handlePack}
              loading={downloadingPack}
              disabled={downloadingPack || loading}
              testId="support-tools-download-pack"
              label="Download the full pack — PDF"
              loadingLabel="Preparing the pack…"
              icon={Archive}
              size="lg"
            />
            <a
              href="#tools"
              data-testid="support-tools-scroll-link"
              className="h-12 px-5 rounded-md font-extrabold text-sm inline-flex items-center justify-center gap-1.5 text-[#1c2d3d] hover:text-[#0f1e2c]"
            >
              Or pick one <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-3 text-xs text-[#4a6984]/65" data-testid="support-tools-privacy-note">
            Blank templates · no student data · no sign-in required.
          </p>
          {stats?.should_display && stats.cumulative_count >= 50 ? (
            <p
              className="mt-2 text-xs text-[#4a6984]/80 inline-flex items-center gap-1.5"
              data-testid="support-tools-counter"
            >
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: "#3EB489" }}
              />
              {stats.cumulative_count.toLocaleString()} downloads to date
            </p>
          ) : null}
        </section>

        <section id="tools" className="mt-14 scroll-mt-8">
          <div className="flex items-baseline justify-between gap-3 mb-5 flex-wrap">
            <h2 className="font-black text-2xl sm:text-3xl text-[#1c2d3d] tracking-tight">
              The tools
            </h2>
            {!loading && !error && tools.length > 0 && (
              <p className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70">
                {tools.length} printable template{tools.length === 1 ? "" : "s"}
              </p>
            )}
          </div>
          {loading ? (
            <div
              className="flex items-center gap-2 text-[#4a6984]/75"
              data-testid="support-tools-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading the tools list…
            </div>
          ) : error ? (
            <p
              className="text-sm text-rose-700"
              data-testid="support-tools-error"
            >
              {error}
            </p>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              data-testid="support-tools-grid"
            >
              {tools.map((t) => (
                <ToolCard
                  key={t.slug}
                  tool={t}
                  downloading={downloadingSlug === t.slug}
                  onDownload={handleSingle}
                />
              ))}
            </div>
          )}
        </section>

        {/* Soft newsletter line — never gates a download */}
        <section
          data-testid="support-tools-newsletter"
          className="mt-16 rounded-xl border border-[#4a6984]/15 bg-white px-6 py-5 sm:px-8 sm:py-6"
        >
          <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            Optional · skip if you like
          </p>
          <p className="mt-2 font-extrabold text-[#1c2d3d] text-lg leading-snug">
            Want more free classroom resources?
          </p>
          <p className="mt-1 text-sm text-[#4a6984]/85">
            We&apos;ll send the occasional new poster, planning tool, or free
            utility. No spam, no sales nonsense.
          </p>
          <Link
            to="/#newsletter"
            data-testid="support-tools-newsletter-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-extrabold text-[#1c2d3d] hover:underline"
          >
            Add your email &rarr;
          </Link>
        </section>

        <section
          className="mt-8 rounded-xl border border-[#4a6984]/15 bg-white px-6 py-5 sm:px-8 sm:py-6 border-l-4 border-l-[#3EB489]"
          data-testid="support-tools-reflection-cta"
        >
          <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            New · year-end
          </p>
          <p className="mt-2 font-extrabold text-[#1c2d3d] text-lg leading-snug">
            Take 5 minutes to reflect on the year that was
          </p>
          <p className="mt-1 text-sm text-[#4a6984]/85 leading-relaxed">
            A tap-only year-end reflection across the seven Ontario
            transferable skills. We&apos;ll send your shape back to you
            when September shows up.
          </p>
          <Link
            to="/reflection"
            data-testid="support-tools-reflection-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-extrabold text-[#1c2d3d] hover:underline"
          >
            Open the reflection &rarr;
          </Link>
        </section>
      </main>
    </div>
  );
}
